/**
 * Commission report CSV export.
 * Columns: Property Address, Sold Price, Total Commission, Share, Sold Date, Settlement Date, Status[, Paid At, Paid By][, Agents].
 * Amounts use 2 dp; no thousands separator so CSV parses correctly.
 * If multiple currencies are added later, include a currency column and use it in UI and exports.
 */

function escapeCsv(value: string): string {
  const s = String(value ?? '');
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Format number for CSV: no thousands separator so commas in content don't break parsing. */
function formatMoney(n: number): string {
  return (n ?? 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  });
}

export type CommissionExportRow = {
  propertyAddress: string;
  soldPrice: number;
  commission: number;
  shareAmount: number;
  soldDate: string;
  settlementDate: string;
  status: string;
  paidAt?: string;
  paidByName?: string;
  agents?: string;
};

export function exportCommissionToCsv(
  rows: CommissionExportRow[],
  filename: string,
  options?: { includeHeaders?: boolean }
): void {
  const includeHeaders = options?.includeHeaders !== false;
  const headers = ['Property Address', 'Sold Price', 'Total Commission', 'Share', 'Sold Date', 'Settlement Date', 'Status'];
  const hasPaidInfo = rows.some((r) => r.paidAt != null || (r.paidByName != null && r.paidByName !== ''));
  if (hasPaidInfo) {
    headers.push('Paid At', 'Paid By');
  }
  const hasAgents = rows.some((r) => r.agents != null && r.agents !== '');
  if (hasAgents) {
    headers.push('Agents');
  }

  const lines: string[] = [];
  if (includeHeaders) {
    lines.push(headers.map(escapeCsv).join(','));
  }
  for (const r of rows) {
    const cells = [
      escapeCsv(r.propertyAddress),
      formatMoney(r.soldPrice),
      formatMoney(r.commission),
      formatMoney(r.shareAmount),
      escapeCsv(formatDate(r.soldDate)),
      escapeCsv(formatDate(r.settlementDate)),
      escapeCsv(r.status),
    ];
    if (hasPaidInfo) {
      cells.push(escapeCsv(r.paidAt ? formatDate(r.paidAt) : ''), escapeCsv(r.paidByName ?? ''));
    }
    if (hasAgents) {
      cells.push(escapeCsv(r.agents ?? ''));
    }
    lines.push(cells.join(','));
  }

  const csv = lines.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function buildExportRows(
  properties: Array<{
    propertyId: string;
    propertyAddress: string;
    soldPrice: number;
    commission: number;
    shareAmount: number;
    soldDate: string;
    settlementDate: string;
    status: string;
    paidAt?: string;
    paidByName?: string;
    agentNames?: string[];
  }>,
  statusFilter: 'all' | 'excluding_cancelled' = 'all'
): CommissionExportRow[] {
  let list = properties;
  if (statusFilter === 'excluding_cancelled') {
    list = list.filter((p) => (p.status ?? 'pending') !== 'cancelled');
  }
  return list.map((p) => ({
    propertyAddress: p.propertyAddress ?? '',
    soldPrice: p.soldPrice ?? 0,
    commission: p.commission ?? 0,
    shareAmount: p.shareAmount ?? 0,
    soldDate: p.soldDate ?? '',
    settlementDate: p.settlementDate ?? '',
    status: p.status === 'paid' ? 'Paid' : p.status === 'cancelled' ? 'Cancelled' : 'Pending',
    paidAt: p.paidAt ?? undefined,
    paidByName: p.paidByName ?? undefined,
    agents: Array.isArray(p.agentNames) ? p.agentNames.join('; ') : undefined,
  }));
}
